import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  CheckSquare, 
  CreditCard, 
  AlertCircle, 
  Users, 
  Building2,
  Package,
  Landmark
} from 'lucide-react';

const Sidebar = ({ user }) => {
  const location = useLocation();

  const navigationItems = [
    {
      title: 'Dashboard',
      path: '/',
      icon: LayoutDashboard,
      roles: ['requester', 'function_approver', 'finance_head', 'finance_team', 'admin', 'accounts']
    },
    {
      title: 'My Requests',
      path: '/purchase-requests',
      icon: FileText,
      roles: ['requester']
    },
    {
      title: 'Approvals',
      path: '/approvals',
      icon: CheckSquare,
      roles: ['function_approver']
    },
    {
      title: 'Finance Review',
      path: '/finance',
      icon: CreditCard,
      roles: ['finance_head']
    },
    {
      title: 'Payment Queue',
      path: '/payments',
      icon: CreditCard,
      roles: ['finance_team']
    },
    {
      title: 'Accounts Review',
      path: '/accounts/purchase-requests',
      icon: Package,
      roles: ['accounts']
    },
    {
      title: 'Admin Controls',
      path: '/admin',
      icon: AlertCircle,
      roles: ['admin']
    },
    {
      title: 'User Management',
      path: '/user-management',
      icon: Users,
      roles: ['admin']
    },
    {
      title: 'Vendors',
      path: '/vendors',
      icon: Building2,
      roles: ['requester', 'function_approver', 'finance_head', 'finance_team', 'admin', 'accounts']
    },
    {
      title: 'Entities',
      path: '/entities',
      icon: Landmark,
      roles: ['admin', 'finance_head', 'finance_team', 'function_approver']
    }
  ];

  const filteredNavigation = navigationItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 min-h-screen fixed left-0 top-0 flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-gray-200">
        <Link to="/" className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              Corp Pay
            </h1>
            <p className="text-xs text-gray-500">Payment System</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {filteredNavigation.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                active
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className={`w-5 h-5 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
              <span className="font-medium">{item.title}</span>
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 rounded-lg">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <span className="text-indigo-600 font-semibold text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 truncate capitalize">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
